/*
 * Card size bar — sizes row at the bottom of the product card image.
 * - Click on an available size adds the variant to the cart and opens the cart drawer.
 * - Horizontal scroll with arrows (RTL aware).
 * - Colors are sibling products: when a color swatch is clicked (event "color-swatch:change"
 *   dispatched by color-swatch.js) the sizes of the selected sibling are loaded and rendered.
 */
if (!customElements.get("card-size-bar")) {
  const productCache = new Map();

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || "/";
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .catch((error) => {
          productCache.delete(handle);
          throw error;
        });
      productCache.set(handle, request);
    }
    return productCache.get(handle);
  };

  // Ascending sizes (first = right side in RTL), regardless of the product's option order:
  // numbers ascending, letter sizes XXS → 5XL, unknown values keep their order at the end.
  // Same logic as the Liquid sort in snippets/card-size-bar.liquid.
  const LETTER_RANKS = {
    XXXS: 5, "3XS": 5, XXS: 10, "2XS": 10, XS: 20, S: 30, M: 40, L: 50, XL: 60,
    XXL: 70, "2XL": 70, XXXL: 80, "3XL": 80, XXXXL: 90, "4XL": 90, XXXXXL: 95, "5XL": 95,
  };
  const sizeRank = (value, index) => {
    const clean = String(value).trim();
    const letter = LETTER_RANKS[clean.toUpperCase()];
    if (letter !== undefined) return letter;
    // Leading zeros (04, 06) count as numbers too
    if (/^\d+(\.\d+)?$/.test(clean)) return Math.round(Number(clean) * 10) + 1000;
    return 1000000 + index;
  };
  const sortSizes = (values) =>
    values
      .map((value, index) => ({ value, rank: sizeRank(value, index), index }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((item) => item.value);

  customElements.define(
    "card-size-bar",
    class CardSizeBar extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.card = this.closest("color-swatch");
        this.track = this.querySelector(".card-size-bar__track");
        this.errorEl = this.querySelector(".card-size-bar__error");
        this.prevButton = this.querySelector(".card-size-bar__arrow--prev");
        this.nextButton = this.querySelector(".card-size-bar__arrow--next");
        this.currentHandle = this.dataset.productHandle;
        this.sizeOptionNames = (this.dataset.sizeOptionNames || "")
          .split("|")
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);
        this.loading = false;

        this.addEventListener("click", this.onClick.bind(this));
        this.track.addEventListener("scroll", () => this.updateArrows(), { passive: true });
        if (window.ResizeObserver) {
          new ResizeObserver(() => this.updateArrows()).observe(this.track);
        }
        requestAnimationFrame(() => this.updateArrows());

        this.onSiblingChange = this.onSiblingChange.bind(this);
        this.card?.addEventListener("color-swatch:change", this.onSiblingChange);
      }

      disconnectedCallback() {
        this.card?.removeEventListener("color-swatch:change", this.onSiblingChange);
        this.initialized = false;
      }

      get isRtl() {
        return getComputedStyle(this.track).direction === "rtl";
      }

      onClick(event) {
        const target = event.target.closest("button");
        if (!target || !this.contains(target)) return;
        event.preventDefault();
        event.stopPropagation();

        if (target.dataset.direction) {
          this.scrollTrack(Number(target.dataset.direction));
          return;
        }
        if (target.getAttribute("aria-disabled") === "true" || this.loading) return;
        if (target.dataset.variantId) this.addToCart(target.dataset.variantId, target);
      }

      /* ---------- Scroll arrows ---------- */
      updateArrows() {
        if (!this.track) return;
        const inner = this.track.parentElement;
        const items = this.track.querySelectorAll(".card-size-bar__size");
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        let contentWidth = 0;
        items.forEach((item) => (contentWidth += item.getBoundingClientRect().width));
        contentWidth += gap * Math.max(items.length - 1, 0);

        // Arrows only when the sizes don't fit the full bar width (arrows excluded),
        // so a tiny overflow caused by the arrows themselves never shows them
        const overflow = contentWidth > inner.clientWidth + 1;
        this.classList.toggle("card-size-bar--overflow", overflow);
        [this.prevButton, this.nextButton].forEach((button) => {
          if (button) button.hidden = !overflow;
        });
        if (!overflow) {
          if (this.track.scrollLeft !== 0) this.track.scrollLeft = 0;
          this.classList.remove("card-size-bar--can-prev", "card-size-bar--can-next");
          return;
        }

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        // In RTL scrollLeft runs from 0 to negative values
        const position = Math.abs(this.track.scrollLeft);
        const canPrev = position > 1;
        const canNext = position < maxScroll - 1;
        if (this.prevButton) this.prevButton.disabled = !canPrev;
        if (this.nextButton) this.nextButton.disabled = !canNext;
        // Used for the fade hint on touch devices (no arrows there)
        this.classList.toggle("card-size-bar--can-prev", canPrev);
        this.classList.toggle("card-size-bar--can-next", canNext);
      }

      /*
       * Page-by-page scroll aligned to whole sizes (independent of screen width):
       * next -> the first size that is not fully visible becomes the first visible size;
       * prev -> the last size hidden before the start becomes the last visible size.
       */
      scrollTrack(direction) {
        const track = this.track;
        const items = [...track.querySelectorAll(".card-size-bar__size")];
        if (!items.length) return;

        const rtl = this.isRtl;
        const trackRect = track.getBoundingClientRect();
        const view = track.clientWidth;
        const maxScroll = track.scrollWidth - view;
        const current = Math.abs(track.scrollLeft);
        const tolerance = 2;

        // Positions relative to the visible start edge of the track (right edge in RTL)
        const startOf = (el) => {
          const rect = el.getBoundingClientRect();
          return rtl ? trackRect.right - rect.right : rect.left - trackRect.left;
        };
        const endOf = (el) => startOf(el) + el.getBoundingClientRect().width;

        let target;
        if (direction > 0) {
          const next = items.find((el) => endOf(el) > view + tolerance);
          target = next ? current + startOf(next) : maxScroll;
        } else {
          const hidden = items.filter((el) => startOf(el) < -tolerance);
          const prev = hidden[hidden.length - 1];
          target = prev ? current + endOf(prev) - view : 0;
        }

        target = Math.round(Math.min(Math.max(target, 0), maxScroll));
        track.scrollTo({ left: rtl ? -target : target, behavior: "smooth" });
      }

      /* ---------- Sibling products (colors) ---------- */
      onSiblingChange(event) {
        const handle = event.detail?.handle;
        if (!handle || handle === this.currentHandle) return;
        this.currentHandle = handle;
        this.showError(false);
        this.classList.add("card-size-bar--updating");

        fetchProduct(handle)
          .then((product) => {
            if (this.currentHandle !== handle) return;
            this.renderProduct(product);
          })
          .catch(() => {
            if (this.currentHandle !== handle) return;
            this.setEnabled(false);
          })
          .finally(() => {
            if (this.currentHandle === handle) this.classList.remove("card-size-bar--updating");
          });
      }

      renderProduct(product) {
        const options = product.options || [];
        const sizeOption = options.find((option) =>
          this.sizeOptionNames.includes(String(option.name).trim().toLowerCase()),
        );

        // Same rule as the Liquid side: only products whose single option is the size
        if (!sizeOption || options.length !== 1) {
          this.setEnabled(false);
          return;
        }

        const key = `option${sizeOption.position}`;
        const fragment = document.createDocumentFragment();

        sortSizes(sizeOption.values).forEach((value) => {
          const variant = product.variants.find((item) => item[key] === value);
          if (!variant) return;

          const button = document.createElement("button");
          button.type = "button";
          button.className = "card-size-bar__size";
          if (variant.available) {
            button.dataset.variantId = variant.id;
            button.setAttribute("aria-label", this.label("labelAdd", value));
          } else {
            button.classList.add("card-size-bar__size--unavailable");
            button.setAttribute("aria-disabled", "true");
            button.setAttribute("aria-label", this.label("labelUnavailable", value));
          }

          const label = document.createElement("span");
          label.className = "card-size-bar__label";
          label.textContent = value;
          button.appendChild(label);
          fragment.appendChild(button);
        });

        this.track.replaceChildren(fragment);
        this.track.setAttribute("aria-label", sizeOption.name);
        this.track.scrollLeft = 0;
        this.setEnabled(true);
        requestAnimationFrame(() => this.updateArrows());
      }

      // When disabled the bar is hidden and the theme's quick add is shown again
      setEnabled(enabled) {
        this.hidden = !enabled;
        this.card?.classList.toggle("card--has-size-bar", enabled);
      }

      label(key, size) {
        return (this.dataset[key] || "").replace("__SIZE__", size);
      }

      /* ---------- Add to cart ---------- */
      addToCart(variantId, button) {
        this.loading = true;
        this.showError(false);
        button.classList.add("is-loading");

        const cart = document.querySelector("cart-drawer");
        cart?.setActiveElement?.(document.activeElement);

        const config = typeof fetchConfig === "function"
          ? fetchConfig("javascript")
          : { method: "POST", headers: { Accept: "application/javascript" } };
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", 1);
        if (cart && typeof cart.getSectionsToRender === "function") {
          formData.append("sections", cart.getSectionsToRender().map((section) => section.id));
          formData.append("sections_url", window.location.pathname);
        }
        config.body = formData;

        fetch(window.routes?.cart_add_url || "/cart/add", config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === "function") {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: "card-size-bar",
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.showError(response.description || response.message);
              return;
            }

            if (typeof publish === "function") {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "card-size-bar",
                productVariantId: variantId,
                cartData: response,
              });
            }

            const isCartPage = document.body.classList.contains("template-cart");
            if (!cart || cart.dataset.cartType === "page" || isCartPage) {
              window.location = window.routes?.cart_url || "/cart";
              return;
            }

            cart.classList.remove("is-empty");
            cart.querySelector(".wt-cart--empty")?.classList.remove("wt-cart--empty");
            const isClosedCart = !document.body.classList.contains("page-overlay-cart-on");
            cart.renderContents(response, isClosedCart);
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error || "");
          })
          .finally(() => {
            this.loading = false;
            setTimeout(() => button.classList.remove("is-loading"), 200);
          });
      }

      showError(message) {
        if (!this.errorEl) return;
        clearTimeout(this.errorTimer);
        this.errorEl.textContent = message || "";
        this.errorEl.hidden = !message;
        if (message) {
          this.errorTimer = setTimeout(() => this.showError(false), 5000);
        }
      }
    },
  );
}
